import { useLazyQuery, useMutation } from '@apollo/client';
import { Box, Button, Grid, TextField } from '@mui/material';
import MD5 from 'crypto-js/md5';
import { mapKeys, snakeCase } from 'lodash-es';
import queryString from 'query-string';
import React, { useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { generatePath } from 'react-router-dom';
import * as XLSX from 'xlsx';

import { CREATE_PIXEL, SEARCH_PIXELS } from '../../api/graphql/pixels';
import { API_PIXEL_GENERATOR, API_PIXEL_GENERATOR_SEARCH } from '../../constants/apiUrls';
import { useConfig } from '../../hooks/useConfig';
import { useSnackBar } from '../../hooks/useSnackBar';
import { snakeCaseKeys } from '../../util/object';
import posthog from '../../util/posthog';
import { getRequestJSON, postRequestJSON } from '../../util/services';
import InputErrorMessages from '../shared/InputErrorMessages';
import {
  GENERAL_SCRIPT,
  HOME_PAGE_SCRIPT,
  LANDING_PAGE_SCRIPT,
  OTHER_SCRIPT,
  TRACK_CONVERSION_SCRIPT,
} from './PixelConstants';
import PixelExport from './PixelExport';
import PixelTags from './PixelTags';
import { defaultPixelGeneratorFormInputValues, Pixel, PixelGeneratorFormInputs } from './PixelTypes';

function PixelGenerator() {
  const form = useForm<PixelGeneratorFormInputs>({
    defaultValues: {
      ...defaultPixelGeneratorFormInputValues,
    },
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = form;

  const { field: clientNameField } = useController({
    name: 'clientName',
    rules: {
      required: 'Please enter Client Name.',
    },
    control,
  });

  const { field: domainField } = useController({
    name: 'clientDomain',
    rules: {
      required: 'Please enter Domain',
    },
    control,
  });

  const { field: pixelIdField } = useController({
    name: 'pixelId',
    control,
  });

  const { field: createdAtField } = useController({
    name: 'createdAt',
    control,
  });

  const [htmlGeneralTags, setHtmlGeneralTags] = useState<string>();
  const [htmlLandingPageTags, setHtmlLandingPageTags] = useState<string>();
  const [htmlHomePageTags, setHtmlHomePageTags] = useState<string>();
  const [htmlOtherTags, setHtmlOtherTags] = useState<string>();
  const [htmlTrackConversionTags, setHtmlTrackConversionTags] = useState<string>();
  const [existingPixels, setExistingPixels] = useState<Pixel[]>();
  const [selectedRow, setSelectedRow] = useState<string>();

  const { apiHost, graphQLApiHost } = useConfig();

  const { showSnackBar } = useSnackBar();

  const useGraphQL = graphQLApiHost.includes('localhost');
  const [generatePixelGQL, { data: generatePixelData, error: generatedPixelError }] = useMutation(CREATE_PIXEL);
  const [searchPixelsGQL, { data: searchPixelsData, error: searchPixelsError }] = useLazyQuery(SEARCH_PIXELS);

  const fillTags = (pixelId: any) => {
    setHtmlGeneralTags(GENERAL_SCRIPT.replace('%PIXEL_ID%', pixelId));
    setHtmlLandingPageTags(LANDING_PAGE_SCRIPT.replace('%PIXEL_ID%', pixelId));
    setHtmlHomePageTags(HOME_PAGE_SCRIPT.replace('%PIXEL_ID%', pixelId));
    setHtmlOtherTags(OTHER_SCRIPT.replace('%PIXEL_ID%', pixelId));
    setHtmlTrackConversionTags(TRACK_CONVERSION_SCRIPT.replace('%PIXEL_ID%', pixelId));
  };

  const generatePixel = async (fields) => {
    const timeZone = 'America/New_York';
    const now = new Date();
    const nowLocale = now.toLocaleString('en-US', { timeZone });
    const hash = MD5(`${fields.clientName}${fields.clientDomain}${now.getMilliseconds()}`);

    const payload = {
      ...snakeCaseKeys(fields),
      pixel_id: hash.toString(),
      created_at: nowLocale,
    };
    const pixelId = hash.toString() ? hash.toString() : fields.pixelId;
    try {
      setValue('createdAt', nowLocale);
      setValue('pixelId', pixelId);
      if (pixelId) {
        fillTags(pixelId);
      }

      posthog.capture('pixel_generated', { client_name: fields.clientName, client_domain: fields.clientDomain });

      if (useGraphQL) {
        await generatePixelGQL({
          variables: {
            clientName: payload.client_name,
            clientDomain: payload.client_domain,
            pixelId: payload.pixel_id,
          },
        });
        if (generatedPixelError) {
          // eslint-disable-next-line no-console
          console.log(`Error at PixelGenerator Service: ${JSON.stringify(generatedPixelError)}`);
        }
        if (generatePixelData) {
          const createdAtResponse = new Date(generatePixelData.createPixel.createdAt).toLocaleString('en-US', {
            timeZone,
          });
          setValue('createdAt', createdAtResponse);
        }
      } else {
        const response = await postRequestJSON(apiHost, API_PIXEL_GENERATOR, payload);
        if (response.model) {
          const createdAtResponse = new Date(response.model.created_at).toLocaleString('en-US', { timeZone });
          setValue('createdAt', createdAtResponse);
        }
      }
    } catch (e) {
      posthog.captureException(e instanceof Error ? e : new Error('pixel_generate_failed'));
      // eslint-disable-next-line no-console
      console.info(e);
      showSnackBar('Pixel ID generated but failed to save it.', 'warning', {
        event: 'pixel_save_error',
        client_name: fields.clientName,
        client_domain: fields.clientDomain,
        pixel_id: pixelId,
      });
    }
  };

  const searchPixel = async (fields) => {
    posthog.capture('pixel_searched', { client_name: fields.clientName, client_domain: fields.clientDomain });
    if (fields.clientName || fields.clientDomain) {
      let pixels: any[];
      if (useGraphQL) {
        await searchPixelsGQL({
          variables: {
            clientName: fields.clientName,
            clientDomain: fields.clientDomain,
          },
        });
        if (searchPixelsError) {
          // eslint-disable-next-line no-console
          console.log(`Error at PixelGenerator Service: ${JSON.stringify(searchPixelsError)}`);
        }
        if (searchPixelsData) {
          if (searchPixelsData.searchPixels) {
            const snakePixels = [];
            searchPixelsData.searchPixels.forEach((p) => {
              const snakePixel = mapKeys(p, (value, key) => snakeCase(key));
              snakePixels.push(snakePixel);
            });
            pixels = snakePixels;
          }
        }
      } else {
        pixels = await getRequestJSON(
          apiHost,
          generatePath(API_PIXEL_GENERATOR_SEARCH, {
            '*': `?${queryString.stringify({
              clientName: fields.clientName,
              clientDomain: fields.clientDomain,
            })}`,
          })
        );
      }
      setExistingPixels(pixels);
      setValue('createdAt', '');
      setValue('pixelId', '');
      setHtmlGeneralTags('');
      setHtmlLandingPageTags('');
      setHtmlHomePageTags('');
      setHtmlOtherTags('');
      setHtmlTrackConversionTags('');
    }
  };

  const handleRowClick = async (currentTarget, id) => {
    setSelectedRow(id);
    const selectedPixel = existingPixels.find((p) => p.pixel_id === id);
    if (selectedPixel) {
      const { pixel_id: pixelId } = selectedPixel;
      setValue('clientName', selectedPixel.client_name);
      setValue('clientDomain', selectedPixel.client_domain);
      setValue('pixelId', pixelId);
      setValue('createdAt', new Date(selectedPixel.created_at).toLocaleString());
      fillTags(pixelId);
    }
  };

  const noDataToExport = !existingPixels || existingPixels.length === 0;
  const exportPixels = async () => {
    if (noDataToExport) return;
    posthog.capture('pixels_exported', { pixel_count: existingPixels.length });

    const rows = existingPixels.map((row) => ({
      client_name: row.client_name,
      client_domain: row.client_domain,
      pixel_id: row.pixel_id,
      created_at: new Date(row.created_at).toLocaleString(),
      general_tags: GENERAL_SCRIPT.replace('%PIXEL_ID%', row.pixel_id),
      landing_page_tags: LANDING_PAGE_SCRIPT.replace('%PIXEL_ID%', row.pixel_id),
      home_page_tags: HOME_PAGE_SCRIPT.replace('%PIXEL_ID%', row.pixel_id),
      other_tags: OTHER_SCRIPT.replace('%PIXEL_ID%', row.pixel_id),
      track_conversion_tags: TRACK_CONVERSION_SCRIPT.replace('%PIXEL_ID%', row.pixel_id),
    }));

    const columnNames = [
      'Client Name',
      'Client Domain',
      'Pixel ID',
      'Created At',
      'Base Script Tags',
      'Landing Page Tags',
      'Home Page Tags',
      'Other Tags',
      'Track Conversion Tags',
    ];
    const colsSize = [
      { width: 32 },
      { width: 32 },
      { width: 32 },
      { width: 32 },
      { width: 64 },
      { width: 64 },
      { width: 64 },
      { width: 64 },
      { width: 64 },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    ws['!cols'] = colsSize;
    XLSX.utils.book_append_sheet(wb, ws, 'Pixels');
    XLSX.utils.sheet_add_aoa(ws, [columnNames], { origin: 'A1' });
    const fileName = `${getValues('clientName')}-${getValues('clientDomain')}-Pixels.xlsx`;
    XLSX.writeFile(wb, fileName, { compression: true });
  };

  const isSelected = (id) => selectedRow && id === selectedRow;
  const pixelGeneratorDisabled = !(apiHost.includes('-qa') || apiHost.includes('localhost'));

  return (
    <Box p={2}>
      <Box>
        <Box p={2} sx={{ background: 'white', borderRadius: '5px' }}>
          <Grid container>
            <Grid item xs={5} pr={1}>
              <Grid container>
                <Grid item xs={12}>
                  <Box my={2} sx={{ width: '100%' }}>
                    <InputErrorMessages name="clientName" errors={errors} />
                  </Box>
                  <TextField
                    name={clientNameField.name}
                    label="Client Name"
                    onChange={clientNameField.onChange}
                    onBlur={clientNameField.onBlur}
                    inputRef={clientNameField.ref}
                    value={clientNameField.value || ''}
                    sx={{ width: '100%' }}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <Box my={2} sx={{ width: '100%' }}>
                    <InputErrorMessages name="clientDomain" errors={errors} />
                  </Box>
                  <TextField
                    name={domainField.name}
                    label="Domain"
                    onChange={domainField.onChange}
                    onBlur={domainField.onBlur}
                    inputRef={domainField.ref}
                    value={domainField.value || ''}
                    sx={{ width: '100%' }}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <Box my={2} sx={{ width: '100%' }}>
                    <InputErrorMessages name="pixelId" errors={errors} />
                  </Box>
                  <TextField
                    name={pixelIdField.name}
                    label="Pixel ID"
                    onChange={pixelIdField.onChange}
                    onBlur={pixelIdField.onBlur}
                    inputRef={pixelIdField.ref}
                    value={pixelIdField.value || ''}
                    sx={{ width: '100%' }}
                    disabled
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <Box my={2} sx={{ width: '100%' }}>
                    <InputErrorMessages name="createdAt" errors={errors} />
                  </Box>
                  <TextField
                    name={createdAtField.name}
                    label="Created At"
                    onChange={createdAtField.onChange}
                    onBlur={createdAtField.onBlur}
                    inputRef={createdAtField.ref}
                    value={createdAtField.value || ''}
                    sx={{ width: '100%' }}
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={7}>
              <PixelExport existingPixels={existingPixels} isSelected={isSelected} handleRowClick={handleRowClick} />
            </Grid>
          </Grid>
          <Grid container mt={2}>
            <Grid item xs={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit(generatePixel)}
                disabled={pixelGeneratorDisabled}
              >
                Generate Pixel
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit(searchPixel)}
                disabled={pixelGeneratorDisabled}
              >
                Search Existing Pixel IDs
              </Button>
            </Grid>
            <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit(exportPixels)}
                disabled={noDataToExport}
              >
                Export Pixels
              </Button>
            </Grid>
          </Grid>
          <PixelTags
            htmlGeneralTags={htmlGeneralTags}
            htmlLandingPageTags={htmlLandingPageTags}
            htmlHomePageTags={htmlHomePageTags}
            htmlOtherTags={htmlOtherTags}
            htmlTrackConversionTags={htmlTrackConversionTags}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default PixelGenerator;
